import * as Auth from 'aws-amplify/auth'
import { TGroups, TUser } from 'models/user.model'
import Services from 'services'
import { TGlobalStore } from '..'
import { IRegister } from 'models/register.model'
import { GROUPS } from 'util/enums/userGroup'
import { getUserRole } from 'util/role'
import { firstLoginKey, safeLS } from 'util/localstorage'
import { TCognitoUserInfo } from 'models/cognito.model'
import type { StoreApi } from 'zustand'

export interface IUserState {
  user: TUser | undefined
  register: (IRegister & { status: 'completed' | 'pending' }) | undefined
  loadingUser: boolean
  loadingRegister: boolean
  isFirstLogin: boolean
  setFirstLogin: (v: boolean) => void
  isLogged: () => Promise<boolean>
  setUser: (user: TUser) => void
  getUser: () => Promise<void>
  getRegister: () => Promise<void>
  logout: () => void
  hasRole: (group: TGroups) => boolean
  hasHighPrivileges: () => boolean
  isHighLevelUser: () => boolean
  isAdmin: () => boolean
  isCompanyOwner: () => boolean
  isManager: () => boolean
  isAutoRemediationManager: () => boolean
  avatarChanged: boolean
  setAvatarChanged: (boolean) => void
  avatar: string
  setAvatar: (avatar: string) => void
  getAvatar: () => Promise<void>
}

const userSlice = (
  set: StoreApi<TGlobalStore>['setState'],
  get: StoreApi<TGlobalStore>['getState'],
): IUserState => ({
  user: undefined,
  register: undefined,
  loadingUser: true,
  loadingRegister: true,
  isFirstLogin: true,
  setFirstLogin: v => {
    const email = get().user?.email
    if (v === false) safeLS.set(firstLoginKey(email), 'true')
    if (v === true) safeLS.del(firstLoginKey(email))
    set(s => ({ ...s, isFirstLogin: v }))
  },
  setUser: user => set(state => ({ ...state, user })),
  getUser: async () => {
    set(state => ({ ...state, loadingUser: true }))

    const cognitoUser = (await Auth.fetchUserAttributes().catch(() => ({}))) as TCognitoUserInfo

    if (!Object.keys(cognitoUser || {}).length) {
      set(state => ({ ...state, user: undefined, loadingUser: false }))
      return
    }

    const session = await Auth.fetchAuthSession().catch(() => null)
    const tokenPayload = session?.tokens?.accessToken?.payload

    const cognitoGroups = (tokenPayload?.['cognito:groups'] || []) as TGroups[]
    const groups = cognitoGroups.filter((g): g is TGroups =>
      Object.values(GROUPS).includes(g as TGroups),
    )

    const user: TUser = {
      name: cognitoUser.name || '',
      phone: cognitoUser.phone_number || '',
      email: cognitoUser.email || '',
      email_verified: cognitoUser.email_verified === true,
      companyId: cognitoUser['custom:company'] || '',
      groups,
      role: getUserRole({ cognitoUser, groups }),
    }

    const seen = safeLS.get(firstLoginKey(user.email)) === 'true'

    set(state => ({ ...state, user, loadingUser: false, isFirstLogin: !seen }))
  },
  getRegister: async () => {
    try {
      set(state => ({ ...state, loadingRegister: true }))
      const response = await Services.Auth.getRegister()
      const status = response.address ? 'completed' : 'pending'
      set(state => ({ ...state, register: { ...response, status } }))
    } catch {
      set(state => ({ ...state, register: undefined, loadingRegister: false }))
    } finally {
      set(state => ({ ...state, loadingRegister: false }))
    }
  },
  isLogged: async () => {
    try {
      const cognitoUser = await Auth.getCurrentUser()
      return !!cognitoUser
    } catch {
      return false
    }
  },
  logout: async () => {
    await Auth.signOut()
    set(
      state => ({
        ...state,
        user: undefined,
        loadingUser: false,
        userScore: null,
        register: undefined,
      }),
      true,
    )
  },
  hasRole: (group: TGroups) => {
    const { user } = get()
    return !!user?.groups?.includes(group)
  },
  hasHighPrivileges: () => {
    const { user } = get()
    return !!user?.groups?.some(role =>
      [GROUPS.ADMIN, GROUPS.COMPANY_OWNER, GROUPS.MANAGER].includes(role),
    )
  },
  isHighLevelUser: () => get().hasHighPrivileges(),
  isCompanyOwner: () => get().hasRole(GROUPS.COMPANY_OWNER),
  isManager: () => get().hasRole(GROUPS.MANAGER),
  isAutoRemediationManager: () => get().hasRole(GROUPS.AUTO_REMEDIATION_MANAGER),
  isAdmin: () => get().hasRole(GROUPS.ADMIN),
  avatarChanged: false,
  setAvatarChanged: avatarChanged => set(state => ({ ...state, avatarChanged })),
  avatar: '',
  setAvatar: avatar => set(state => ({ ...state, avatar })),
  getAvatar: async (): Promise<void> => {
    try {
      const avatarBlob = await Services.Auth.getImageBlob()
      if (avatarBlob) set({ avatar: avatarBlob })
    } catch {
      set({ avatar: '' })
    }
  },
})

export default userSlice
